// Where a view change puts the focus, kept as STATE (#1472's rule for the
// drawer's rider pager, here for the full-page checkout).
//
// It was a ref that an effect with no dependency list read, set after an
// await (a rider's save): on a slow phone an effect still pending from an
// earlier render could run first, find no target yet (or the old one),
// spend the request, and the render that showed the target never moved the
// focus — the hold-ended words were never focused (the focus fell to the
// page as Next turned off), a rider the end of the step could not save on
// the other ride left the focus on the page. Now:
//   * the request is set in the same update as the view it points at, so
//     the render that shows its target is the one whose effect acts on it
//     (an earlier render's effect only ever sees an earlier request);
//   * each request is acted on once (its sequence number), and counts as
//     done only once its target was found and focused;
//   * a target that is still not there after one more render: the fallback
//     (the words beside the button, else the step's heading), never <body>.
import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { focusRequestTurn } from "../../state/focus-request";

export type FocusRequest<T extends object> = T & { readonly n: number };

/**
 * `first`: the request the component opens with (null: none).
 * `aim(want)`: moves the focus for a request; true once its target HAS
 * the focus (document.activeElement, checked — or nothing needed doing),
 * false while it is not there or would not take it.
 * `fallback()`: where the focus goes when the target has not appeared by
 * the render after.
 * `deps`: what the targets depend on besides the request (the step, the
 * card on screen); a fixed-length list, as for any effect.
 * Returns the function that asks (null withdraws a request not acted on).
 */
export function useFocusRequest<T extends object>(
  first: T | null,
  aim: (want: FocusRequest<T>) => boolean,
  fallback: () => HTMLElement | null,
  deps: DependencyList,
): (next: T | null) => void {
  const seq = useRef(first ? 1 : 0);
  const [request, setRequest] = useState<FocusRequest<T> | null>(() => (first ? { ...first, n: 1 } : null));
  // The last request acted on.
  const done = useRef(0);
  // The request that has waited one render for its target (that render is
  // asked for here, so it comes even when nothing else changes).
  const [waited, setWaited] = useState(0);
  const ask = useCallback((next: T | null) => setRequest(next ? { ...next, n: ++seq.current } : null), []);
  useEffect(() => {
    if (!request) return;
    const turn = focusRequestTurn(request, done.current, waited, () => aim(request));
    if (turn === "none") return;
    if (turn === "wait") {
      setWaited(request.n);
      return;
    }
    done.current = request.n;
    if (turn === "fallback") fallback()?.focus();
    // `aim` and `fallback` are this render's (an effect runs the latest
    // render's closure); the request, the wait and `deps` decide when.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, waited, ...deps]);
  return ask;
}
